import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

interface NotificationsContextValue {
  unreadCount: number;
  ready: boolean;
}

const NotificationsContext = createContext<NotificationsContextValue>({ unreadCount: 0, ready: false });

export const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [ready, setReady] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  // Keep current unsubscribe refs so we don't leak listeners between auth changes
  const unsubAuthRef = useRef<() => void>();
  const unsubNotifRef = useRef<() => void>();

  useEffect(() => {
    // Wait for auth to be ready before starting any listeners
    const unsubscribe = onAuthStateChanged(auth, (u: User | null) => {
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Don't start listeners until auth is ready
    if (!authReady) return;

    // cleanup existing notif listener
    if (unsubNotifRef.current) {
      try { unsubNotifRef.current(); } catch {}
      unsubNotifRef.current = undefined;
    }

    const u = auth.currentUser;
    if (!u) {
      setUnreadCount(0);
      setReady(true);
      return;
    }

    // Only create listener if user is authenticated
    console.log('NotificationsContext: Setting up listener for user:', u.uid);
    try {
      const ref = collection(db, 'notifications');
      const q = query(ref, where('recipientId', '==', u.uid));
      unsubNotifRef.current = onSnapshot(q, (snap) => {
        console.log('NotificationsContext: Received', snap.size, 'notifications');
        snap.forEach((d) => { 
          console.log('Notification:', d.id, d.data()); 
        });
        let count = 0;
        snap.forEach((d) => { try { if (!d.data()?.read) count += 1; } catch {} });
        console.log('NotificationsContext: Unread count:', count);
        setUnreadCount(count);
        setReady(true);
      }, (err: any) => {
        console.error('NotificationsContext: Error in snapshot:', err);
        // Silently ignore all permission errors
        setReady(true);
      });
    } catch (e: any) {
      console.error('NotificationsContext: Error setting up listener:', e);
      setReady(true);
    }

    return () => {
      if (unsubNotifRef.current) { try { unsubNotifRef.current(); } catch {} }
    };
  }, [authReady]);

  const value = useMemo(() => ({ unreadCount, ready }), [unreadCount, ready]);
  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationsContext);
